import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { ChevronDown, Leaf, Mail, Phone, MapPin } from "lucide-react";

export default function PrivacyPolicy() {
  const [expandedSection, setExpandedSection] = useState(null);

  const sections = [
    {
      title: "1. Introduction",
      content:
        "Agrivestia ('we', 'us', 'our', or 'Company') operates the Agrivestia platform. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our services. Please read this privacy policy carefully. If you do not agree with our policies and practices, please do not use our Services. We are committed to protecting your privacy and ensuring you have a positive experience on our platform.",
    },
    {
      title: "2. Information We Collect",
      content: `We collect information in various ways, including:

a) Information You Provide Directly:
• Account registration details (name, email, phone, address)
• Payment and billing information
• Profile information and preferences
• Communication with our support team
• Documents for verification purposes (ID, business registration, etc.)

b) Automatically Collected Information:
• Device information (device type, operating system, browser type)
• Usage data (pages visited, time spent, clicks, features used)
• Location data (if you permit)
• Cookie and similar tracking technologies
• IP address and connection information

c) Third-Party Data:
• Information from payment processors for transaction verification
• Data from verification services for KYC (Know Your Customer) compliance
• Information from business partners with your consent`,
    },
    {
      title: "3. How We Use Your Information",
      content: `We use your information for the following purposes:

a) Service Delivery:
• Creating and maintaining your account
• Processing transactions and payments
• Providing customer support and resolving disputes
• Facilitating marketplace transactions between buyers and sellers
• Delivering products and services you request

b) Legal Compliance:
• Verifying your identity and preventing fraud
• Complying with legal obligations and regulations
• AML/KYC (Anti-Money Laundering / Know Your Customer) requirements
• Responding to government requests or legal processes
• Protecting against illegal activities

c) Business Operations:
• Improving and optimizing our platform
• Conducting market research and analytics
• Personalizing your experience
• Sending transactional notifications and account updates
• Monitoring platform security and preventing abuse

d) Marketing and Communications (with your consent):
• Sending promotional offers and updates
• Requesting feedback or surveys
• Newsletters about new features and opportunities
• Agricultural market insights and trends

e) Safety and Security:
• Detecting and preventing fraud, abuse, and unauthorized access
• Protecting the rights, property, and safety of users and the public`,
    },
    {
      title: "4. Data Protection and Security",
      content: `We implement comprehensive security measures to protect your data:

a) Technical Measures:
• End-to-end encryption for sensitive data
• SSL/TLS encryption for data in transit
• Secure password hashing and storage
• Regular security audits and penetration testing
• Firewall protection and intrusion detection systems

b) Organizational Measures:
• Access controls - only authorized personnel access your data
• Employee training on data protection and privacy
• Non-disclosure agreements with all staff and contractors
• Regular data privacy impact assessments

c) Data Retention:
• We retain data only as long as necessary for the purposes outlined in this policy
• Account data is retained for the duration of your membership plus retention periods for legal compliance
• Deleted data is securely destroyed using industry-standard methods

While we strive to protect your information, no security system is impenetrable. We cannot guarantee absolute security of your data transmitted over the internet.`,
    },
    {
      title: "5. Data Sharing",
      content: `We may share your information with:

a) Essential Service Providers:
• Payment processors (for transaction processing)
• Cloud hosting providers (for data storage)
• Email service providers (for communications)
• Analytics providers (for platform improvement)

b) Legal Requirements:
• Law enforcement or government agencies (when required by law)
• Courts or legal proceedings
• Regulatory bodies (Central Bank, SEC, etc.)

c) Business Partners:
• Verification services for identity and background checks
• Insurance providers (for transaction protection)
• Business partners with your explicit consent

We ensure all third parties have adequate data protection agreements in place. We do not sell your personal data to third parties for their marketing purposes. Your data will not be shared without your explicit consent, except as required by law.`,
    },
    {
      title: "6. Your Rights and Choices",
      content: `You have the following rights regarding your personal data:

a) Access:
• Right to request a copy of your personal data
• Right to access your data in a portable format

b) Correction:
• Right to correct inaccurate or incomplete information
• Right to update your profile information anytime

c) Deletion:
• Right to request deletion of your data (subject to legal retention requirements)
• Right to withdraw consent for non-essential data processing

d) Opt-Out:
• Unsubscribe from marketing communications
• Disable cookies (though some features may not work properly)
• Adjust privacy settings in your account

e) Data Portability:
• Right to receive your data in a structured, portable format
• Right to transfer your data to another service provider

To exercise these rights, contact us at privacy@agrivestia.com. We will respond to your request within 30 days.`,
    },
    {
      title: "7. Children's Privacy",
      content:
        "Our Services are not intended for children under 18 years of age. We do not knowingly collect personal information from children. If we become aware that we have collected data from a child under 18, we will promptly delete such information and terminate the child's account. Parents or guardians who believe their child has provided information to Agrivestia should contact us immediately at privacy@agrivestia.com.",
    },
    {
      title: "8. International Data Transfers",
      content:
        "Your information may be processed and stored in countries other than your country of residence, which may have different data protection laws. By using Agrivestia, you consent to the transfer of your information to countries outside your country of residence, including countries that may not have data protection laws equivalent to those in your home country. We implement safeguards to ensure adequate protection of your data.",
    },
    {
      title: "9. Cookies and Tracking Technologies",
      content: `We use cookies and similar tracking technologies to:

• Enhance user experience and remember preferences
• Analyze usage patterns and improve functionality
• Prevent fraud and ensure security
• Show relevant content and advertisements

You can control cookies through your browser settings. However, disabling cookies may affect functionality. We also respect Do Not Track signals, though we may still collect data for security and fraud prevention.`,
    },
    {
      title: "10. Contact Us",
      content:
        "If you have questions, concerns, or requests regarding this Privacy Policy or our privacy practices, please contact us:\n\nEmail: privacy@agrivestia.com\nPhone: +234 (0) 123-456-7890\nAddress: Agrivestia Office, [City], Nigeria\n\nWe will respond to all inquiries within 30 days. You also have the right to lodge a complaint with the relevant data protection authority in your jurisdiction.",
    },
    {
      title: "11. Changes to This Privacy Policy",
      content:
        "We may update this Privacy Policy from time to time to reflect changes in our practices, technology, or legal requirements. Any significant changes will be communicated via email or a notice on our platform. Your continued use of Agrivestia after such modifications constitutes your acceptance of the updated Privacy Policy. We encourage you to review this policy periodically.",
    },
  ];

  return (
    <div style={{ fontFamily: "var(--font-sans)", minHeight: "100vh", background: "var(--color-gray-50)" }}>
      <Helmet>
        <title>Privacy Policy — Agrivestia</title>
      </Helmet>

      {/* Header */}
      <div style={{ background: "var(--gradient-primary)", color: "white", padding: "60px 32px", textAlign: "center" }}>
        <div style={{ maxWidth: "800px", margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "12px", marginBottom: "16px" }}>
            <Leaf size={32} />
            <span style={{ fontSize: "24px", fontWeight: 800, letterSpacing: "-0.02em" }}>AGRIVESTIA</span>
          </div>
          <h1 style={{ fontSize: "42px", fontWeight: 800, marginBottom: "16px", letterSpacing: "-0.02em" }}>Privacy Policy</h1>
          <p style={{ fontSize: "16px", opacity: 0.9 }}>Last updated: February 24, 2026</p>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: "900px", margin: "0 auto", padding: "60px 32px" }}>
        <div style={{ marginBottom: "48px", padding: "24px", background: "white", borderRadius: "12px", border: "1px solid var(--color-gray-100)", boxShadow: "0 4px 6px rgba(0,0,0,0.05)" }}>
          <p style={{ color: "var(--color-gray-700)", lineHeight: 1.8, fontSize: "15px" }}>
            This Privacy Policy explains how Agrivestia collects, uses, discloses, and protects your information when you use our platform. 
            We are committed to maintaining the highest standards of data protection and transparency. This policy applies to all users, 
            including farmers, traders, investors, and other stakeholders in our agricultural marketplace.
          </p>
        </div>

        {/* Sections */}
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {sections.map((section, index) => (
            <div key={index} style={{ background: "white", border: "1px solid var(--color-gray-100)", borderRadius: "12px", overflow: "hidden", boxShadow: "0 2px 4px rgba(0,0,0,0.05)" }}>
              <button
                onClick={() => setExpandedSection(expandedSection === index ? null : index)}
                style={{
                  width: "100%",
                  padding: "20px",
                  background: "white",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  transition: "all 0.2s",
                }}
                onMouseEnter={e => e.currentTarget.style.background = "var(--color-gray-50)"}
                onMouseLeave={e => e.currentTarget.style.background = "white"}
              >
                <h3 style={{ fontSize: "16px", fontWeight: 700, color: "var(--color-gray-900)", textAlign: "left" }}>
                  {section.title}
                </h3>
                <ChevronDown
                  size={20}
                  style={{
                    color: "var(--color-primary)",
                    transition: "transform 0.3s",
                    transform: expandedSection === index ? "rotate(180deg)" : "rotate(0deg)",
                    flexShrink: 0,
                  }}
                />
              </button>

              {expandedSection === index && (
                <div style={{ padding: "24px", borderTop: "1px solid var(--color-gray-100)", background: "var(--color-gray-50)" }}>
                  <p style={{ color: "var(--color-gray-700)", lineHeight: 1.8, fontSize: "14px", whiteSpace: "pre-wrap" }}>
                    {section.content}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Contact CTA */}
        <div style={{ marginTop: "60px", padding: "40px", background: "var(--color-primary-light)", borderRadius: "16px", border: "1px solid var(--color-accent-light)" }}>
          <h2 style={{ fontSize: "20px", fontWeight: 800, color: "var(--color-primary-dark)", marginBottom: "24px" }}>Have Privacy Questions?</h2>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "24px" }}>
            <div style={{ display: "flex", gap: "12px" }}>
              <Mail size={20} style={{ color: "var(--color-primary)", flexShrink: 0, marginTop: "2px" }} />
              <div>
                <div style={{ fontWeight: 600, color: "var(--color-gray-900)", marginBottom: "4px" }}>Email</div>
                <a href="mailto:privacy@agrivestia.com" style={{ color: "var(--color-primary)", textDecoration: "none" }}>
                  privacy@agrivestia.com
                </a>
              </div>
            </div>
            <div style={{ display: "flex", gap: "12px" }}>
              <Phone size={20} style={{ color: "var(--color-primary)", flexShrink: 0, marginTop: "2px" }} />
              <div>
                <div style={{ fontWeight: 600, color: "var(--color-gray-900)", marginBottom: "4px" }}>Phone</div>
                <a href="tel:+234123456789" style={{ color: "var(--color-primary)", textDecoration: "none" }}>
                  +234 (0) 123-456-7890
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Links */}
        <div style={{ marginTop: "60px", paddingTop: "40px", borderTop: "1px solid var(--color-gray-200)", textAlign: "center" }}>
          <p style={{ color: "var(--color-gray-600)", marginBottom: "16px" }}>Related Policies</p>
          <div style={{ display: "flex", justifyContent: "center", gap: "24px", flexWrap: "wrap" }}>
            <a href="/terms-of-service" style={{ color: "var(--color-primary)", textDecoration: "none", fontWeight: 600 }}>
              Terms of Service
            </a>
            <span style={{ color: "var(--color-gray-300)" }}>•</span>
            <a href="/" style={{ color: "var(--color-primary)", textDecoration: "none", fontWeight: 600 }}>
              Back to Home
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
